import { useEffect } from 'react';

import { NOT_FOUND_STATUS_CODE, UNAUTHORIZED_STATUS_CODE } from '@/shared/api/errors';
import { useEmailConsentContext } from '@/shared/contexts/EmailConsentContext';
import { getAttributionPayloadForSignup, getLinkedInAttribution } from '@/shared/lib/attribution';
import { inIframe } from '@/shared/lib/browser';
import { isErrorGuard } from '@/shared/lib/types';
import { hubSpotMethods } from '@/shared/thirdParty/hubSpot';
import { setMixpanelUser } from '@/shared/thirdParty/mixpanel';
import { useRewardfulStore } from '@/shared/thirdParty/rewardful/useRewardfulStore';
import { SentryRask } from '@/shared/thirdParty/sentry';

import { useCreateAccount } from './useCreateAccount';
import { useCreateUser } from './useCreateUser';
import { useCurrentUser } from './useCurrentUser';
import { useGetAccount } from './useGetAccount';
import { useSyncGA4ClientId } from './useSyncGA4ClientId';
import { useSyncGoogleAdsAttribution } from './useSyncGoogleAdsAttribution';
import { useSyncLinkedInAttribution } from './useSyncLinkedInAttribution';
import { useUpdateUser } from './useUpdateUser';

type UseGetOrCreateUser = {
  isSocial: boolean;
  tiktokCallback: () => void;
};

export const useGetOrCreateUser = ({ isSocial, tiktokCallback }: UseGetOrCreateUser) => {
  const referralId = useRewardfulStore((state) => state.referralId);
  const { emailConsent } = useEmailConsentContext();
  const { data: user, error: userError, isSuccess: isUserSuccess } = useCurrentUser();
  const { data: account, error: accountError } = useGetAccount(Boolean(user));
  const { mutate: createUser } = useCreateUser({
    isSocial,
  });
  const { mutate: createAccount } = useCreateAccount();
  const { mutate: updateUser } = useUpdateUser();

  useSyncGA4ClientId(user);
  useSyncGoogleAdsAttribution(user);
  useSyncLinkedInAttribution(user);

  useEffect(() => {
    if (isErrorGuard(userError, NOT_FOUND_STATUS_CODE)) {
      if (inIframe()) {
        tiktokCallback();
      } else {
        createUser(
          {
            attribution: getAttributionPayloadForSignup(),
            linkedin: getLinkedInAttribution(),
            referral_id: referralId || '',
          },
          {
            onSuccess: () => {
              if (emailConsent) {
                updateUser({
                  email_consent: emailConsent,
                });
              }
            },
          },
        );
      }
    }
  }, [createUser, updateUser, emailConsent, referralId, tiktokCallback, userError]);

  useEffect(() => {
    if (isErrorGuard(accountError, UNAUTHORIZED_STATUS_CODE)) {
      createAccount({ email: user?.email, referral: referralId || '' });
    }
  }, [accountError, createAccount, referralId, user?.email]);

  useEffect(() => {
    if (isUserSuccess) {
      setMixpanelUser(user.id, user.email, user.email_type);
      SentryRask.initUser({ id: user.id, email: user.email });

      void hubSpotMethods.initUser(user.email);
    }
  }, [isUserSuccess, user]);

  return {
    account,
    isAuthenticated: !!user && !!account,
    user,
  };
};
